/**
 * Shared schedule pill label derivation. Matches Explore's getScheduleBadge
 * behavior for the plain (no-active-date-window) case so Plan and Explore show
 * the same pill for the same event.
 */
import { format } from "date-fns";

export function getScheduleLabel(event: {
  start_date?: string | null;
  end_date?: string | null;
  schedule_notes?: string | null;
}): string | null {
  if (event.start_date) {
    const s = new Date(event.start_date + "T12:00:00");
    if (event.end_date && event.end_date !== event.start_date) {
      const e = new Date(event.end_date + "T12:00:00");
      return `${format(s, "MMM d")}–${format(e, "MMM d, yyyy")}`;
    }
    return format(s, "MMM d, yyyy");
  }

  const notes = (event.schedule_notes || "").trim();
  if (!notes) return "Daily";
  const lower = notes.toLowerCase();

  const dayMap: Record<string, string> = {
    mon: "Mon", monday: "Mon",
    tue: "Tue", tues: "Tue", tuesday: "Tue",
    wed: "Wed", weds: "Wed", wednesday: "Wed",
    thu: "Thu", thur: "Thu", thurs: "Thu", thursday: "Thu",
    fri: "Fri", friday: "Fri",
    sat: "Sat", saturday: "Sat",
    sun: "Sun", sunday: "Sun",
  };
  const order = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  const rangeMatch = lower.match(/\b(mon(?:day)?|tue(?:s|sday)?|wed(?:s|nesday)?|thu(?:r|rs|rsday)?|fri(?:day)?|sat(?:urday)?|sun(?:day)?)\s*[-–—to ]+\s*(mon(?:day)?|tue(?:s|sday)?|wed(?:s|nesday)?|thu(?:r|rs|rsday)?|fri(?:day)?|sat(?:urday)?|sun(?:day)?)\b/);
  if (rangeMatch) {
    const a = dayMap[rangeMatch[1]];
    const b = dayMap[rangeMatch[2]];
    if (a && b) return `${a}–${b}`;
  }

  const found = new Set<string>();
  for (const key of Object.keys(dayMap)) {
    const re = new RegExp(`\\b${key}\\b`, "i");
    if (re.test(lower)) found.add(dayMap[key]);
  }
  if (found.size > 0) return order.filter((d) => found.has(d)).join(", ");

  if (/\bweekends?\b/.test(lower)) return "Sat–Sun";
  if (/\bweekdays?\b/.test(lower)) return "Mon–Fri";
  if (/\b(daily|every\s*day|always|year-?round|dawn\s*to\s*dusk|24\/7|anytime|open\s*daily)\b/.test(lower)) return "Daily";
  return "Varies";
}